import { AuthOnFile, JCodeDetails } from "../data-model/medicalPaOrdersModel";
import { DocumentTypesForOrder } from "../enums/documentTypes";

/**
 * Returns all J-codes in the order that share the same auth letter document
 * (i.e. have an auth_letter document with the given doc path).
 * Used on the authorisation details screen to show all procedures covered by
 * that letter in the Procedure Information section.
 *
 * @param authOnFile - Auth-on-file subtree (`entries`, falling back to `auth_entries`).
 * @param letterDocPath - The doc_path of the auth letter (from the clicked row).
 * @returns Array of JCodeDetails that have that auth letter; empty if none match.
 */
export const getJCodesWithAuthLetterDocPath = (
  authOnFile: AuthOnFile | null | undefined,
  letterDocPath: string | null | undefined,
): JCodeDetails[] => {
  return getJCodesWithAuthLetterDocPathFromAuthOnFile(
    authOnFile,
    letterDocPath,
  );
};

/**
 * Returns all J-codes from the auth-on-file data that share the same auth
 * letter path (from either `documents` or nested `auth_letter.auth_letter_path`).
 * Operates directly on the {@link AuthOnFile} object instead of the full order.
 *
 * @param authOnFile - The auth-on-file record (`entries`, falling back to `auth_entries`).
 * @param letterDocPath - The doc_path of the auth letter (from the clicked row).
 * @returns Array of JCodeDetails that have that auth letter; empty if none match.
 */
export const getJCodesWithAuthLetterDocPathFromAuthOnFile = (
  authOnFile: AuthOnFile | null | undefined,
  letterDocPath: string | null | undefined,
): JCodeDetails[] => {
  const authEntries = authOnFile?.entries ?? authOnFile?.auth_entries;
  if (!authEntries?.length || !letterDocPath?.trim()) {
    return [];
  }

  const allJCodes = authEntries.flatMap((entry) => entry?.j_codes ?? []);

  return allJCodes.filter((jCode) => {
    const docs = jCode?.documents ?? [];
    const hasMatchingDocument = docs.some(
      (d) =>
        d?.doc_type === DocumentTypesForOrder.AUTH_LETTER &&
        d?.doc_path === letterDocPath,
    );
    if (hasMatchingDocument) {
      return true;
    }
    return jCode?.auth_letter?.auth_letter_path === letterDocPath;
  });
};
